import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Box,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { type SxProps, type Theme } from "@mui/material/styles";
import { useCallback, useMemo, useState } from "react";
import {
  NotificationChannel,
  NotificationEventType,
  type NotificationShadowStatus,
} from "../../generated/graphql";
import { enumKeys } from "../../utils/enum";
import NotificationShadowSummaryPanel from "./notification-shadow-summary-panel";
import { SHADOW_STATUS_ORDER } from "./notification-shadow-status";
import NotificationShadowTable from "./notification-shadow-table";

const styles = {
  subtitle: { mt: 0.5, mb: 3, maxWidth: 640 },
  filterBar: { mb: 3, alignItems: "center", flexWrap: "wrap" },
  filterControl: { minWidth: 200 },
} satisfies Record<string, SxProps<Theme>>;

/** Sentinel select value meaning "no restriction on this dimension". */
const ALL_VALUE = "ALL";

const EVENT_TYPE_OPTIONS = enumKeys(NotificationEventType).map(
  (key) => NotificationEventType[key],
);
const CHANNEL_OPTIONS = enumKeys(NotificationChannel).map(
  (key) => NotificationChannel[key],
);

type EnumFilterSelectProps = {
  readonly label: string;
  readonly value: string | undefined;
  readonly options: readonly string[];
  readonly onChange: (value: string | undefined) => void;
};

/**
 * Shared "All / one of these enum values" filter dropdown for the three
 * shadow-comparison filters. Works over plain strings -- each caller owns
 * the cast back to its concrete enum type, matching the notification-cutover
 * page's `Select` (a generic type param here would have MUI's `Select` infer
 * its value type as the widened `string`, defeating the point).
 */
const EnumFilterSelect = ({
  label,
  value,
  options,
  onChange,
}: EnumFilterSelectProps) => (
  <FormControl size="small" sx={styles.filterControl}>
    <InputLabel>{label}</InputLabel>
    <Select
      label={label}
      value={value ?? ALL_VALUE}
      onChange={(event) => {
        const next = event.target.value;
        onChange(next === ALL_VALUE ? undefined : next);
      }}
    >
      <MenuItem value={ALL_VALUE}>All</MenuItem>
      {options.map((option) => (
        <MenuItem key={option} value={option}>
          {option}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);

/**
 * Internal tool visualizing the notification engine's shadow-mode comparison
 * results (TMS-1660): the legacy path stays authoritative while the engine
 * computes what it would send, and this page shows where the two diverged.
 * A MATCH self-prunes at write time (row deleted, only a metric kept), so
 * only PENDING (awaiting its counterpart), MISMATCH (diverged), and ORPHAN
 * (one side never reported) rows are ever queryable here.
 */
const NotificationShadow = () => {
  const [status, setStatus] = useState<NotificationShadowStatus | undefined>(
    undefined,
  );
  const [eventType, setEventType] = useState<NotificationEventType | undefined>(
    undefined,
  );
  const [channel, setChannel] = useState<NotificationChannel | undefined>(
    undefined,
  );
  const [refreshKey, setRefreshKey] = useState(0);

  const filter = useMemo(
    () => ({ status, eventType, channel }),
    [status, eventType, channel],
  );

  const handleRefresh = useCallback(() => {
    setRefreshKey((key) => key + 1);
  }, []);

  const handleStatusChange = useCallback((value: string | undefined) => {
    setStatus(value as NotificationShadowStatus | undefined);
  }, []);

  const handleEventTypeChange = useCallback((value: string | undefined) => {
    setEventType(value as NotificationEventType | undefined);
  }, []);

  const handleChannelChange = useCallback((value: string | undefined) => {
    setChannel(value as NotificationChannel | undefined);
  }, []);

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="h5">Notification shadow comparisons</Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={handleRefresh}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Stack>
      <Typography color="text.secondary" variant="body2" sx={styles.subtitle}>
        Shadow-mode comparison evidence between the legacy notification path and
        the engine while a channel is being validated in parallel.
      </Typography>

      <NotificationShadowSummaryPanel filter={filter} refreshKey={refreshKey} />

      <Stack direction="row" spacing={2} sx={styles.filterBar}>
        <EnumFilterSelect
          label="Status"
          value={status}
          options={SHADOW_STATUS_ORDER}
          onChange={handleStatusChange}
        />
        <EnumFilterSelect
          label="Event type"
          value={eventType}
          options={EVENT_TYPE_OPTIONS}
          onChange={handleEventTypeChange}
        />
        <EnumFilterSelect
          label="Channel"
          value={channel}
          options={CHANNEL_OPTIONS}
          onChange={handleChannelChange}
        />
      </Stack>

      <NotificationShadowTable filter={filter} refreshKey={refreshKey} />
    </Box>
  );
};

export default NotificationShadow;
